/* Search V2 - Unified Implementation */

// Development logging flag (reads from global for controlled debugging)
let SEARCH_V2_DEBUG = false;
try {
  SEARCH_V2_DEBUG = !!(window && (window.debugSearch || window.DEBUG_SEARCH || window.debugAll));
} catch (e) { /* noop */ }
const searchV2Log = (...args) => {
  try { if (SEARCH_V2_DEBUG) console.log(...args); } catch (e) { /* noop */ }
};

searchV2Log('🔍 Search V2 Unified script loading...');

class SearchV2Unified {
  constructor() {
    // Modal elements
    this.searchButton = document.querySelector('[data-search-v2-toggle]');
    this.searchModal = document.querySelector('[data-search-modal-v2]');
    this.closeButton = document.querySelector('[data-search-close]');
    this.clearButton = document.querySelector('[data-search-clear]');
    this.backdrop = document.querySelector('[data-search-backdrop]');
    this.searchForm = document.querySelector('.search-form-v2');
    
    // Predictive search elements
    this.searchInput = document.querySelector('#search-input-v2');
    this.resultsContainer = document.querySelector('#predictive-search-results-v2');
    this.predictiveEnabled = !!this.resultsContainer;
    
    // State
    this.isOpen = false;
    this.lastFocusedElement = null;
    this.searchTerm = '';
    this.seedTerm = '';
    this.cachedResults = {};
    this.abortController = new AbortController();
    this.debounceTimer = null;
    this.previewTimer = null; // debounce for previewing suggestions
    this.currentRenderedTerm = ''; // track which term the right panel shows
    
    // Cached container refs for accessibility
    this.mainContent = document.getElementById('MainContent');
    this.modalContent = this.searchModal ? this.searchModal.querySelector('.search-modal-v2__content') : null;
    
    searchV2Log('🔍 SearchV2Unified elements found:', {
      searchButton: !!this.searchButton,
      searchModal: !!this.searchModal,
      searchInput: !!this.searchInput,
      resultsContainer: !!this.resultsContainer
    });
    
    this.init();
  }
  
  init() {
    if (!this.searchButton || !this.searchModal) {
      console.error('❌ Required modal elements not found');
      return;
    }
    
    if (!this.searchInput) {
      console.error('❌ Required search input not found');
      return;
    }
    
    this.bindEvents();
    searchV2Log('✅ SearchV2Unified initialized');
  }
  
  bindEvents() {
    // Modal events
    this.searchButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.openModal();
    });
    
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeModal());
    }
    
    // Outside click-to-close is intentionally disabled for mobile reliability
    // and to avoid accidental dismissals. Users can close via Close button or Escape.
    if (this.backdrop) {
      // no-op: leave backdrop clicks inert
    }
    
    // Search input events (only when predictive is enabled)
    if (this.predictiveEnabled) {
      this.searchInput.addEventListener('input', (e) => {
        this.onInput(e);
      });
      
      this.searchInput.addEventListener('focus', () => {
        if (this.searchTerm && this.resultsContainer && this.resultsContainer.innerHTML.trim()) {
          this.showResults();
        }
      });
      
      this.searchInput.addEventListener('blur', (e) => {
        // After focus settles, only hide if focus moved outside the results/modal
        setTimeout(() => {
          if (this.interactingWithResults) return;
          const ae = document.activeElement;
          if ((this.resultsContainer && this.resultsContainer.contains(ae)) ||
              (this.modalContent && this.modalContent.contains(ae) && ae !== this.searchInput)) {
            return; // keep results visible when navigating within modal/results
          }
          this.hideResults();
        }, 0);
      });
      
      this.searchInput.addEventListener('keydown', (e) => {
        // Ensure results are visible when navigating with arrows from the input
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && this.resultsContainer && this.resultsContainer.innerHTML.trim()) {
          this.showResults();
        }
        this.onKeydown(e);
      });

      // Delegate clicks inside results: suggestions re-query instead of navigating
      if (this.resultsContainer) {
        // Mark interactions so input blur doesn't hide results prematurely
        this.resultsContainer.addEventListener('mousedown', () => {
          this.interactingWithResults = true;
        });
        this.resultsContainer.addEventListener('touchstart', () => {
          this.interactingWithResults = true;
        }, { passive: true });
        document.addEventListener('mouseup', () => {
          this.interactingWithResults = false;
        });
        document.addEventListener('touchend', () => {
          this.interactingWithResults = false;
        }, { passive: true });

        this.resultsContainer.addEventListener('click', (e) => {
          const link = e.target && e.target.closest('a');
          if (!link) return;
          // If user clicks a suggestion, intercept and re-query
          if (link.classList.contains('search-v2__suggestion')) {
            e.preventDefault();
            const text = link.textContent ? link.textContent.trim() : '';
            if (!text) return;
            // Keep suggestions list intact; update only products
            this.previewTerm = text;
            this.getSearchResults(text, { productsOnly: true, term: text });
          }
          // Product cards keep default behavior (navigate)
        });

        // Keyboard support when focus is inside the results
        this.resultsContainer.addEventListener('keydown', (e) => {
          const target = e.target && e.target.closest('a');
          if (!target) return;
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            this.navigateResults(e.key === 'ArrowDown' ? 'down' : 'up');
            return;
          }
          if (e.key === 'Enter') {
            if (target.classList.contains('search-v2__suggestion') || target.dataset.suggestion === 'true') {
              e.preventDefault();
              const text = target.textContent ? target.textContent.trim() : '';
              if (text) {
                // Apply selection but keep suggestions static; update only products
                this.previewTerm = text;
                this.getSearchResults(text, { productsOnly: true, term: text });
              }
            }
          }
        });

        // Live preview on focus within results (covers Tab/Shift+Tab and programmatic focus)
        this.resultsContainer.addEventListener('focusin', (e) => {
          const link = e.target && e.target.closest('a');
          if (!link) return;
          this.maybePreviewSuggestion(link);
        });

        // Live preview on hover
        this.resultsContainer.addEventListener('mouseover', (e) => {
          const link = e.target && e.target.closest('a');
          if (!link) return;
          this.maybePreviewSuggestion(link);
        });
      }
    }
    
    
    
    // Clear button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.searchInput.value = '';
        this.searchInput.focus();
        this.toggleClearButton();
        this.hideResults();
      });
    }
    
    // Form submission
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', (e) => {
        if (!this.searchInput.value.trim()) {
          e.preventDefault();
          this.searchInput.focus();
        }
      });
    }
    
    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeModal();
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'k' && !this.isOpen) {
        e.preventDefault();
        this.openModal();
      }
      
       // Focus trap inside the modal when open
       if (e.key === 'Tab' && this.isOpen) {
         const nodeList = this.modalContent ? this.modalContent.querySelectorAll('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, audio[controls], video[controls], [contenteditable], [tabindex]:not([tabindex="-1"])') : null;
         if (!nodeList || nodeList.length === 0) return;
         const elements = Array.from(nodeList).filter(el => el && (el.offsetParent !== null || el === document.activeElement));
         if (!elements.length) return;
         const first = elements[0];
         const last = elements[elements.length - 1];
         if (e.shiftKey && document.activeElement === first) {
           e.preventDefault();
           last.focus();
         } else if (!e.shiftKey && document.activeElement === last) {
           e.preventDefault();
           first.focus();
         }
       }
    });
    
    // Outside click-to-close disabled by design; explicit close controls remain
  }
  
  // Modal functionality
  openModal() {
    if (this.isOpen) return;
    
    this.lastFocusedElement = document.activeElement;
    this.isOpen = true;
    
    this.searchModal.classList.remove('hidden');
    this.searchModal.classList.add('active');
    this.searchModal.setAttribute('aria-hidden', 'false');
    this.searchButton.setAttribute('aria-expanded', 'true');
    
    // Inert background content while modal is open
    if (this.mainContent) {
      this.mainContent.setAttribute('inert', '');
      this.mainContent.setAttribute('aria-hidden', 'true');
    }
    
    requestAnimationFrame(() => {
      this.searchInput.focus();
    });
    
    // Use class-based scroll lock (no inline styles)
    document.body.classList.add('scroll-locked');
    document.documentElement.classList.add('scroll-locked');
    searchV2Log('🔍 Search modal opened');
  }
  
  closeModal() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    this.hideResults();
    
    this.searchModal.classList.remove('active');
    this.searchModal.setAttribute('aria-hidden', 'true');
    this.searchButton.setAttribute('aria-expanded', 'false');
    
    // Restore background interactivity
    if (this.mainContent) {
      this.mainContent.removeAttribute('inert');
      this.mainContent.removeAttribute('aria-hidden');
    }
    
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }
    
    // Remove class-based scroll lock
    document.body.classList.remove('scroll-locked');
    document.documentElement.classList.remove('scroll-locked');
    
    setTimeout(() => {
      if (!this.isOpen) {
        this.searchModal.classList.add('hidden');
      }
    }, 200);
    
    searchV2Log('🔍 Search modal closed');
  }
  
  // Predictive search functionality
  onInput(event) {
    if (!this.predictiveEnabled) return;
    const newSearchTerm = event.target.value.trim();
    this.seedTerm = newSearchTerm;
    this.toggleClearButton();
    
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    if (!newSearchTerm) {
      this.searchTerm = '';
      this.hideResults();
      return;
    }
    
    this.debounceTimer = setTimeout(() => {
      if (newSearchTerm !== this.searchTerm) {
        this.searchTerm = newSearchTerm;
        this.getSearchResults(newSearchTerm);
      }
    }, 300);
  }
  
  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.navigateResults('down');
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigateResults('up');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectResult();
        break;
      case 'Escape':
        this.hideResults();
        break;
    }
  }
  
  async getSearchResults(searchTerm, options = {}) {
    const cacheKey = searchTerm.toLowerCase();
    if (this.cachedResults[cacheKey]) {
      this.renderResults(this.cachedResults[cacheKey], options);
      return;
    }
    
    // Only show global loading on full renders; keep suggestions visible during product-only updates
    if (!options.productsOnly) {
      this.showLoading();
    }
    
    this.abortController.abort();
    this.abortController = new AbortController();
    
    try {
      // Try multiple URL patterns for Shopify predictive search
      const possibleUrls = [
        `${window.routes?.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
        `/search/suggest?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
        `/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,query`,
        `/search?q=${encodeURIComponent(searchTerm)}&view=json`
      ];
      
      let response = null;
      let searchUrl = null;
      
      for (const url of possibleUrls) {
        if (url.startsWith('undefined')) continue;
        
        try {
          searchV2Log('🔍 Trying URL:', url);
          response = await fetch(url, {
            signal: this.abortController.signal,
            headers: {
              'Accept': 'text/html,application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          
          if (response.ok) {
            searchUrl = url;
            break;
          }
        } catch (e) {
          searchV2Log('❌ URL failed:', url, e.message);
          continue;
        }
      }
      
      if (!response || !response.ok) {
        throw new Error('All search URLs failed');
      }
      
      searchV2Log('✅ Successful URL:', searchUrl);
      
      const contentType = response.headers.get('content-type');
      let searchData;
      
      if (contentType && contentType.includes('application/json')) {
        // Handle JSON response
        const jsonData = await response.json();
        searchData = this.parseJsonResponse(jsonData);
      } else {
        // Handle HTML response
        const htmlText = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');
        const resultsSection = doc.querySelector('#shopify-section-predictive-search') || doc.body;
        searchData = this.extractSearchData(resultsSection);
      }
      
      this.cachedResults[cacheKey] = searchData;
      this.renderResults(searchData, options);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      
      console.error('❌ Search request failed:', error);
      this.renderFallbackResults(searchTerm, options);
    }
  }
  
  parseJsonResponse(jsonData) {
    const data = {
      queries: [],
      products: [],
      totalResults: 0
    };
    
    // Handle different JSON response formats
    if (jsonData.resources) {
      // Shopify predictive search format
      if (jsonData.resources.queries) {
        data.queries = jsonData.resources.queries.map(q => ({
          text: q.text,
          url: `/search?q=${encodeURIComponent(q.text)}`
        }));
      }
      
      if (jsonData.resources.products) {
        data.products = jsonData.resources.products.map(p => ({
          title: p.title,
          vendor: p.vendor,
          price: p.price ? `$${(p.price / 100).toFixed(2)}` : null,
          image: p.featured_image,
          url: p.url
        }));
      }
    }
    
    data.totalResults = data.queries.length + data.products.length;
    return data;
  }
  
  extractSearchData(resultsSection) {
    const data = {
      queries: [],
      products: [],
      totalResults: 0
    };
    
    // Extract queries/suggestions
    const queryElements = resultsSection.querySelectorAll('#predictive-search-results-queries-list li, [data-predictive-search-type="query"]');
    queryElements.forEach(el => {
      const link = el.querySelector('a');
      if (link) {
        const text = link.textContent.trim();
        const url = link.href;
        if (text && url) {
          data.queries.push({ text, url });
        }
      }
    });
    
    // Extract products
    const productElements = resultsSection.querySelectorAll('#predictive-search-results-products-list li, [data-predictive-search-type="product"]');
    productElements.forEach(el => {
      const link = el.querySelector('a');
      if (link) {
        const title = el.querySelector('.predictive-search__item-heading, h3, .product-title')?.textContent?.trim();
        const vendor = el.querySelector('.predictive-search__item-vendor')?.textContent?.trim();
        const price = el.querySelector('.price, .money')?.textContent?.trim();
        const image = el.querySelector('img')?.src;
        const url = link.href;
        
        if (title && url) {
          data.products.push({ title, vendor, price, image, url });
        }
      }
    });
    
    data.totalResults = data.queries.length + data.products.length;
    return data;
  }
  
  renderFallbackResults(searchTerm, options = {}) {
    // Create basic suggestions when API fails
    const data = {
      queries: [
        { text: searchTerm, url: `/search?q=${encodeURIComponent(searchTerm)}` }
      ],
      products: [],
      totalResults: 1
    };

    this.renderResults(data, options);
  }
  
  renderResults(data, options = {}) {
    const productsOnly = !!options.productsOnly;
    const termForFooter = options.term || this.searchTerm;
    this.hideLoading();
    
    if (!productsOnly && data.queries.length === 0 && data.products.length === 0) {
      this.renderNoResults();
      return;
    }
    
    // If only products should update, replace just the products grid and footer
    if (productsOnly) {
      const productsGrid = this.resultsContainer.querySelector('.search-v2__products-grid');
      const footerLink = this.resultsContainer.querySelector('.search-v2__footer .search-v2__see-all');
      if (productsGrid) {
        productsGrid.innerHTML = `
          ${data.products.slice(0, 6).map(product => `
            <li>
              <a href="${product.url}" class="search-v2__product-card">
                ${product.image ? `<img src="${product.image}" alt="${product.title}" class="search-v2__product-image" loading="lazy">` : ''}
                <div class="search-v2__product-info">
                  ${product.vendor ? `<div class="search-v2__product-vendor">${product.vendor}</div>` : ''}
                  <h4 class="search-v2__product-title">${product.title}</h4>
                  ${product.price ? `<div class="search-v2__product-price">${product.price}</div>` : ''}
                </div>
              </a>
            </li>
          `).join('')}
          ${data.products.length === 0 ? '<li class="search-v2__no-results">No products found</li>' : ''}
        `;
        if (footerLink) {
          footerLink.href = `/search?q=${encodeURIComponent(termForFooter)}`;
          footerLink.textContent = `See all results for "${termForFooter}"`;
        }
        this.currentRenderedTerm = termForFooter;
        this.showResults();
        return;
      }
      // If no products grid exists yet, fall back to full render below
    }

    const html = `
      <div class="search-v2__grid">
        <div class="search-v2__column search-v2__column--suggestions">
          <h3 class="search-v2__heading">Suggestions</h3>
          <ul class="search-v2__list">
            ${data.queries.map(query => `
              <li>
                <a href="${query.url}" class="search-v2__suggestion" data-suggestion="true">
                  ${this.highlightSearchTerm(query.text)}
                </a>
              </li>
            `).join('')}
            ${data.queries.length === 0 ? '<li class="search-v2__no-results">No suggestions found</li>' : ''}
          </ul>
        </div>
        
        <div class="search-v2__column search-v2__column--products">
          <h3 class="search-v2__heading">Product Suggestions</h3>
          <ul class="search-v2__products-grid">
            ${data.products.slice(0, 6).map(product => `
              <li>
                <a href="${product.url}" class="search-v2__product-card">
                  ${product.image ? `<img src="${product.image}" alt="${product.title}" class="search-v2__product-image" loading="lazy">` : ''}
                  <div class="search-v2__product-info">
                    ${product.vendor ? `<div class="search-v2__product-vendor">${product.vendor}</div>` : ''}
                    <h4 class="search-v2__product-title">${product.title}</h4>
                    ${product.price ? `<div class="search-v2__product-price">${product.price}</div>` : ''}
                  </div>
                </a>
              </li>
            `).join('')}
            ${data.products.length === 0 ? '<li class="search-v2__no-results">No products found</li>' : ''}
          </ul>
        </div>
      </div>
      
      ${data.totalResults > 0 ? `
        <div class="search-v2__footer">
          <a href="/search?q=${encodeURIComponent(termForFooter)}" class="search-v2__see-all">
            See all results for "${termForFooter}"
          </a>
        </div>
      ` : ''}
    `;
    
    this.resultsContainer.innerHTML = html;
    // Update the currently rendered term for preview dedupe
    this.currentRenderedTerm = termForFooter;
    this.showResults();
  }
  
  renderNoResults() {
    this.resultsContainer.innerHTML = `
      <div class="search-v2__no-results">
        <p>No results found for "${this.searchTerm}"</p>
        <a href="/search?q=${encodeURIComponent(this.searchTerm)}" class="search-v2__see-all">
          Search all products
        </a>
      </div>
    `;
    this.currentRenderedTerm = this.searchTerm;
    this.showResults();
  }
  
  highlightSearchTerm(text) {
    if (!this.searchTerm) return text;
    
    const regex = new RegExp(`(${this.searchTerm})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }
  
  showLoading() {
    if (!this.resultsContainer) return;
    this.resultsContainer.innerHTML = `
      <div class="search-v2__loading">
        <div class="search-v2__spinner"></div>
      </div>
    `;
    this.resultsContainer.classList.add('active');
  }
  
  hideLoading() {
    // Loading will be replaced by results
  }
  
  showResults() {
    if (!this.resultsContainer) return;
    this.resultsContainer.classList.add('active');
    this.searchInput.setAttribute('aria-expanded', 'true');
  }
  
  hideResults() {
    if (!this.resultsContainer) return;
    this.resultsContainer.classList.remove('active');
    this.searchInput.setAttribute('aria-expanded', 'false');
  }
  
  toggleClearButton() {
    if (!this.clearButton) return;
    
    if (this.searchInput.value.length > 0) {
      this.clearButton.classList.remove('hidden');
    } else {
      this.clearButton.classList.add('hidden');
    }
  }
  
  navigateResults(direction) {
    const items = this.resultsContainer.querySelectorAll('a');
    if (!items.length) return;
    
    const currentSelected = this.resultsContainer.querySelector('[data-selected="true"]');
    let newIndex = 0;
    
    if (currentSelected) {
      const currentIndex = parseInt(currentSelected.dataset.index) || 0;
      if (direction === 'down') {
        newIndex = currentIndex + 1 >= items.length ? 0 : currentIndex + 1;
      } else {
        newIndex = currentIndex - 1 < 0 ? items.length - 1 : currentIndex - 1;
      }
      currentSelected.removeAttribute('data-selected');
    }
    
    items[newIndex].setAttribute('data-selected', 'true');
    items[newIndex].setAttribute('data-index', newIndex);
    items[newIndex].focus();

    // If the newly selected item is a suggestion, live preview it (immediate for keyboard UX)
    this.maybePreviewSuggestion(items[newIndex], true);
  }
  
  selectResult() {
    const selected = this.resultsContainer.querySelector('[data-selected="true"]');
    if (selected) {
      // If the selected item is a suggestion, re-query instead of navigating
      if (selected.classList && (selected.classList.contains('search-v2__suggestion') || selected.dataset.suggestion === 'true')) {
        const text = selected.textContent ? selected.textContent.trim() : '';
        if (text) {
          this.applySuggestionSelection(text);
          return;
        }
      }
      // Otherwise treat as a normal link (e.g., product)
      window.location.href = selected.href;
    }
  }

  // Apply a suggestion as the current query and fetch results immediately
  applySuggestionSelection(text) {
    // Keep input/seed as-is; update only products for selected suggestion
    this.previewTerm = text;
    this.getSearchResults(text, { productsOnly: true, term: text });
  }

  // Preview suggestion without requiring Enter/click
  maybePreviewSuggestion(anchor, immediate = false) {
    if (!anchor) return;
    const isSuggestion = anchor.classList && (anchor.classList.contains('search-v2__suggestion') || anchor.dataset.suggestion === 'true');
    if (!isSuggestion) return;
    const text = anchor.textContent ? anchor.textContent.trim() : '';
    if (!text || text === this.currentRenderedTerm) return;
    // Do not alter the input or searchTerm; keep seed suggestions visible
    this.previewTerm = text;
    if (immediate) {
      if (text !== this.currentRenderedTerm) this.getSearchResults(text, { productsOnly: true, term: text });
    } else {
      this.schedulePreviewFetch(text);
    }
  }

  schedulePreviewFetch(text) {
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => {
      // Avoid duplicate fetches if results already show this term
      if (text === this.currentRenderedTerm) return;
      this.getSearchResults(text, { productsOnly: true, term: text });
    }, 200);
  }
}

// Single initialization
function initializeSearchV2Unified() {
  if (window.searchV2Unified) {
    searchV2Log('⏭️ SearchV2Unified already initialized');
    return;
  }
  
  searchV2Log('🚀 Initializing SearchV2Unified...');
  window.searchV2Unified = new SearchV2Unified();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSearchV2Unified);
} else {
  initializeSearchV2Unified();
}

// Expose for debugging
window.SearchV2Unified = SearchV2Unified;
window.initializeSearchV2Unified = initializeSearchV2Unified;

searchV2Log('✅ Search V2 Unified script loaded - Single class, no conflicts');